<template>
  <div class="admin-page">
    <div class="admin-toolbar">
      <div class="admin-toolbar__actions">
        <el-input v-model="filters.referrerUserId" placeholder="推荐人ID" clearable class="filter-input" />
        <el-input v-model="filters.refereeUserId" placeholder="被推荐人ID" clearable class="filter-input" />
        <el-input v-model="filters.couponNo" placeholder="券号" clearable class="filter-input" />
        <el-select v-model="filters.couponType" placeholder="券类型" clearable class="filter-select" @change="fetchBindings">
          <el-option label="双享" value="normal" />
          <el-option label="臻享" value="partner" />
          <el-option label="专享" value="cash" />
        </el-select>
        <el-button type="primary" @click="fetchBindings">查询绑定</el-button>
        <el-button @click="fetchCommission">分润流水</el-button>
        <el-button link type="primary" @click="$router.push('/admin/referral-user-overview')">用户全景</el-button>
      </div>
    </div>

    <el-tabs v-model="activeTab">
      <el-tab-pane label="绑定记录" name="bindings">
        <el-table :data="bindings" v-loading="loadingBindings">
          <el-table-column prop="id" label="ID" width="70" />
          <el-table-column prop="couponNo" label="券号" width="130" />
          <el-table-column prop="couponType" label="类型" width="120">
            <template #default="s">{{ couponTypeLabel(s.row.couponType) }}</template>
          </el-table-column>
          <el-table-column label="推荐人" min-width="120">
            <template #default="s">{{ s.row.referrerNickname }} ({{ s.row.referrerUserId }})</template>
          </el-table-column>
          <el-table-column label="被推荐人" min-width="120">
            <template #default="s">{{ s.row.refereeNickname || s.row.refereeUsername }} ({{ s.row.refereeUserId }})</template>
          </el-table-column>
          <el-table-column prop="bonusPaid" label="已赠金" width="80">
            <template #default="s">{{ s.row.bonusPaid === 1 ? '是' : '否' }}</template>
          </el-table-column>
          <el-table-column label="操作" width="220" fixed="right">
            <template #default="s">
              <el-button v-if="s.row.bonusPaid !== 1" link type="primary" @click="retryBonus(s.row)">补发礼遇</el-button>
              <el-button v-if="s.row.bonusPaid === 1" link type="warning" @click="clawbackBonus(s.row)">冲正礼遇</el-button>
              <el-button link type="danger" @click="revokeBinding(s.row)">撤销</el-button>
            </template>
          </el-table-column>
        </el-table>
        <AdminPagination
          v-model:current-page="bindingsPage"
          v-model:page-size="bindingsPageSize"
          :total="bindingsTotal"
          @size-change="fetchBindings"
          @current-change="fetchBindings"
        />
      </el-tab-pane>
      <el-tab-pane label="合伙人分润" name="commission">
        <div class="tab-toolbar">
          <el-select v-model="commissionStatus" placeholder="状态" clearable class="filter-select" @change="fetchCommission">
            <el-option label="预估" value="estimated" />
            <el-option label="待到账" value="pending" />
            <el-option label="已到账" value="settled" />
            <el-option label="已取消" value="cancelled" />
          </el-select>
        </div>
        <el-table :data="commissions" v-loading="loadingCommission">
          <el-table-column prop="id" label="ID" width="70" />
          <el-table-column prop="orderNo" label="订单号" min-width="160" show-overflow-tooltip />
          <el-table-column prop="partnerUserId" label="合伙人" width="90" />
          <el-table-column label="订单(元)" width="100">
            <template #default="s">{{ centsToYuan(s.row.orderAmountCents) }}</template>
          </el-table-column>
          <el-table-column label="分润(元)" width="100">
            <template #default="s">{{ centsToYuan(s.row.commissionCents) }}</template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="90">
            <template #default="s">{{ commissionStatusLabel(s.row.status) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template #default="s">
              <el-button
                v-if="s.row.status === 'pending' || s.row.status === 'estimated' || s.row.status === 'settled'"
                link
                type="danger"
                @click="cancelCommission(s.row)"
              >
                取消
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <AdminPagination
          v-model:current-page="commissionPage"
          v-model:page-size="commissionPageSize"
          :total="commissionTotal"
          @size-change="fetchCommission"
          @current-change="fetchCommission"
        />
      </el-tab-pane>
      <el-tab-pane label="绑券尝试" name="bindAttempts">
        <div class="tab-toolbar">
          <el-input v-model="attemptFilters.userId" placeholder="用户ID" clearable class="filter-input" />
          <el-input v-model="attemptFilters.clientIp" placeholder="IP" clearable class="filter-input" />
          <el-input v-model="attemptFilters.couponNo" placeholder="券号" clearable class="filter-input" />
          <el-select v-model="attemptFilters.success" placeholder="结果" clearable class="filter-select" @change="fetchBindAttempts">
            <el-option label="成功" value="true" />
            <el-option label="失败" value="false" />
          </el-select>
          <el-button type="primary" @click="fetchBindAttempts">查询</el-button>
        </div>
        <el-table :data="bindAttempts" v-loading="loadingBindAttempts">
          <el-table-column prop="id" label="ID" width="70" />
          <el-table-column prop="userId" label="用户" width="90" />
          <el-table-column prop="clientIp" label="IP" width="130" />
          <el-table-column prop="couponNo" label="券号" width="140" />
          <el-table-column label="结果" width="70">
            <template #default="s">
              <el-tag :type="s.row.success ? 'success' : 'danger'" size="small">{{ s.row.success ? '成功' : '失败' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="failReason" label="原因" min-width="180" show-overflow-tooltip />
          <el-table-column label="时间" width="170">
            <template #default="s">{{ formatTime(s.row.createdAt) }}</template>
          </el-table-column>
        </el-table>
        <AdminPagination
          v-model:current-page="attemptsPage"
          v-model:page-size="attemptsPageSize"
          :total="attemptsTotal"
          @size-change="fetchBindAttempts"
          @current-change="fetchBindAttempts"
        />
      </el-tab-pane>
      <el-tab-pane label="操作审计" name="audit">
        <div class="tab-toolbar">
          <el-select v-model="auditAction" placeholder="操作类型" clearable class="filter-select" @change="fetchAudit">
            <el-option label="撤销绑定" value="revoke_binding" />
            <el-option label="冲正礼遇" value="clawback_bonus" />
            <el-option label="解除限制" value="unblock_bonus" />
            <el-option label="取消分润" value="cancel_commission" />
            <el-option label="更新配置" value="update_settings" />
          </el-select>
        </div>
        <el-table :data="auditItems" v-loading="loadingAudit">
          <el-table-column prop="id" label="ID" width="70" />
          <el-table-column prop="action" label="操作" width="140" />
          <el-table-column prop="adminUserId" label="管理员" width="90" />
          <el-table-column prop="targetUserId" label="目标用户" width="90" />
          <el-table-column prop="detail" label="详情" min-width="200" show-overflow-tooltip />
          <el-table-column label="时间" width="170">
            <template #default="s">{{ formatTime(s.row.createdAt) }}</template>
          </el-table-column>
        </el-table>
        <AdminPagination
          v-model:current-page="auditPage"
          v-model:page-size="auditPageSize"
          :total="auditTotal"
          @size-change="fetchAudit"
          @current-change="fetchAudit"
        />
      </el-tab-pane>
      <el-tab-pane label="线上配置" name="settings">
        <el-form v-if="settingsForm" label-width="160px" class="settings-form">
          <el-form-item label="注册后补绑天数">
            <el-input-number v-model="settingsForm.bindWithinDaysAfterReg" :min="0" />
            <span class="field-hint">0 = 仅注册时可绑</span>
          </el-form-item>
          <el-form-item label="推荐人礼遇（分）">
            <el-input-number v-model="settingsForm.referrerBonusCents" :min="0" />
          </el-form-item>
          <el-form-item label="被推荐人礼遇（分）">
            <el-input-number v-model="settingsForm.refereeBonusCents" :min="0" />
          </el-form-item>
          <el-form-item label="双享礼券上限">
            <el-input-number v-model="settingsForm.maxCouponsPerUser" :min="1" />
          </el-form-item>
          <el-form-item label="分润比例（%）">
            <el-input-number v-model="settingsForm.partnerCommissionRate" :min="0" :max="100" />
          </el-form-item>
          <el-form-item label="分润 T+N 天">
            <el-input-number v-model="settingsForm.partnerCommissionSettleDays" :min="1" />
          </el-form-item>
          <el-form-item label="臻享见面礼（分）">
            <el-input-number v-model="settingsForm.partnerBindBonusCents" :min="0" />
          </el-form-item>
          <el-form-item label="礼券名称上限">
            <el-input-number v-model="settingsForm.nameMaxLen" :min="4" :max="40" />
          </el-form-item>
          <el-form-item label="绑券频率/用户/时">
            <el-input-number v-model="settingsForm.bindRateLimitPerUserPerHour" :min="0" />
            <span class="field-hint">0 = 用 yaml 默认</span>
          </el-form-item>
          <el-form-item label="绑券频率/IP/时">
            <el-input-number v-model="settingsForm.bindRateLimitPerIPPerHour" :min="0" />
          </el-form-item>
          <el-form-item label="补绑需实名">
            <el-switch v-model="settingsForm.requireRealNameForBind" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="savingSettings" @click="saveSettings">保存到线上</el-button>
          </el-form-item>
        </el-form>
      </el-tab-pane>
    </el-tabs>

    <div class="partner-grant">
      <h4>臻享资格（仅管理员）</h4>
      <p class="partner-grant__hint">撤销绑定后用户将被标记为不可再绑礼券；可在用户全景页解除限制。已发礼遇可在此页冲正。</p>
      <el-input v-model="partnerUserId" placeholder="用户 ID" style="width: 140px; margin-right: 8px" />
      <el-input v-model="partnerUsername" placeholder="或用户名" style="width: 160px; margin-right: 8px" />
      <el-button type="warning" @click="grantPartner(true)">开通臻享</el-button>
      <el-button @click="grantPartner(false)">关闭臻享</el-button>
    </div>

    <el-dialog v-model="revokeDialogVisible" title="撤销绑定" width="440px" destroy-on-close>
      <p>绑定 #{{ revokeTarget?.id }} · 券号 {{ revokeTarget?.couponNo }}</p>
      <el-checkbox v-model="revokeOptions.clawbackBonus">冲正已发礼遇</el-checkbox>
      <el-checkbox v-model="revokeOptions.cancelCommissions">取消该客户分润</el-checkbox>
      <el-checkbox v-model="revokeOptions.skipBlockRebind">不限制用户再次绑券</el-checkbox>
      <template #footer>
        <el-button @click="revokeDialogVisible = false">取消</el-button>
        <el-button type="danger" :loading="revoking" @click="confirmRevoke">确认撤销</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import AdminPagination from '@/components/AdminPagination.vue'
import {
  adminCancelPartnerCommission,
  adminClawbackReferralBonus,
  adminRetryReferralBonus,
  adminUpdateReferralSettings,
  commissionStatusLabel,
  couponTypeLabel,
  centsToYuan,
  fetchAdminReferralActions,
  fetchAdminReferralBindAttempts,
  fetchReferralSettings
} from '@/utils/referralCoupon'

export default {
  name: 'ReferralBindingsManagement',
  components: { AdminPagination },
  data() {
    return {
      activeTab: 'bindings',
      loadingBindings: false,
      loadingCommission: false,
      loadingAudit: false,
      savingSettings: false,
      bindings: [],
      bindingsTotal: 0,
      bindingsPage: 1,
      bindingsPageSize: 20,
      commissions: [],
      commissionTotal: 0,
      commissionPage: 1,
      commissionPageSize: 20,
      commissionStatus: '',
      bindAttempts: [],
      attemptsTotal: 0,
      attemptsPage: 1,
      attemptsPageSize: 20,
      loadingBindAttempts: false,
      attemptFilters: { userId: '', clientIp: '', couponNo: '', success: '' },
      revokeDialogVisible: false,
      revokeTarget: null,
      revoking: false,
      revokeOptions: { clawbackBonus: true, cancelCommissions: true, skipBlockRebind: false },
      auditItems: [],
      auditTotal: 0,
      auditPage: 1,
      auditPageSize: 20,
      auditAction: '',
      filters: { referrerUserId: '', refereeUserId: '', couponNo: '', couponType: '' },
      partnerUserId: '',
      partnerUsername: '',
      settingsForm: null
    }
  },
  watch: {
    activeTab(val) {
      if (val === 'audit' && !this.auditItems.length) this.fetchAudit()
      if (val === 'bindAttempts' && !this.bindAttempts.length) this.fetchBindAttempts()
      if (val === 'settings' && !this.settingsForm) this.loadSettings()
    }
  },
  created() {
    this.fetchBindings()
  },
  methods: {
    couponTypeLabel,
    commissionStatusLabel,
    centsToYuan,
    formatTime(ts) {
      if (!ts) return '—'
      return new Date(ts * 1000).toLocaleString()
    },
    async fetchBindings() {
      this.loadingBindings = true
      try {
        const res = await axios.post('/api/v1/admin/referral/bindings/list', {
          page: this.bindingsPage,
          pageSize: this.bindingsPageSize,
          referrerUserId: Number(this.filters.referrerUserId) || undefined,
          refereeUserId: Number(this.filters.refereeUserId) || undefined,
          couponNo: this.filters.couponNo || undefined,
          couponType: this.filters.couponType || undefined
        })
        if (res.data.code === 200) {
          this.bindings = res.data.data?.bindings || []
          this.bindingsTotal = res.data.data?.total || 0
        }
      } finally {
        this.loadingBindings = false
      }
    },
    async fetchCommission() {
      this.activeTab = 'commission'
      this.loadingCommission = true
      try {
        const res = await axios.post('/api/v1/admin/referral/commission/list', {
          page: this.commissionPage,
          pageSize: this.commissionPageSize,
          partnerUserId: Number(this.filters.referrerUserId) || undefined,
          status: this.commissionStatus || undefined
        })
        if (res.data.code === 200) {
          this.commissions = res.data.data?.items || []
          this.commissionTotal = res.data.data?.total || 0
        }
      } finally {
        this.loadingCommission = false
      }
    },
    async fetchAudit() {
      this.loadingAudit = true
      try {
        const data = await fetchAdminReferralActions(this.auditPage, this.auditPageSize, this.auditAction)
        this.auditItems = data.items || []
        this.auditTotal = data.total || 0
      } catch (e) {
        this.$message.error(e.message || '加载审计失败')
      } finally {
        this.loadingAudit = false
      }
    },
    async loadSettings() {
      try {
        const s = await fetchReferralSettings(true)
        this.settingsForm = {
          bindWithinDaysAfterReg: s.bindWithinDaysAfterReg ?? 7,
          referrerBonusCents: s.referrerBonusCents ?? 500,
          refereeBonusCents: s.refereeBonusCents ?? 1000,
          maxCouponsPerUser: s.maxCouponsPerUser ?? 10,
          nameMaxLen: s.nameMaxLen ?? 20,
          partnerCommissionRate: s.partnerCommissionRate ?? 10,
          partnerCommissionSettleDays: s.partnerCommissionSettleDays ?? 7,
          partnerBindBonusCents: s.partnerBindBonusCents ?? 0,
          bindRateLimitPerUserPerHour: s.bindRateLimitPerUserPerHour ?? 5,
          bindRateLimitPerIPPerHour: s.bindRateLimitPerIPPerHour ?? 20,
          requireRealNameForBind: !!s.requireRealNameForBind
        }
      } catch (e) {
        this.$message.error(e.message || '加载配置失败')
      }
    },
    async saveSettings() {
      this.savingSettings = true
      try {
        await adminUpdateReferralSettings(this.settingsForm)
        this.$message.success('线上配置已保存')
      } catch (e) {
        this.$message.error(e.message || '保存失败')
      } finally {
        this.savingSettings = false
      }
    },
    async grantPartner(isPartner) {
      const uid = Number(this.partnerUserId)
      const username = (this.partnerUsername || '').trim()
      if (!uid && !username) {
        this.$message.warning('请输入用户 ID 或用户名')
        return
      }
      const payload = { isPartner }
      if (uid) payload.userId = uid
      if (username) payload.username = username
      const res = await axios.post('/api/v1/admin/partner/grant', payload)
      if (res.data.code === 200) {
        this.$message.success(res.data.message)
        if (this.activeTab === 'audit') this.fetchAudit()
      } else {
        this.$message.error(res.data.message || '操作失败')
      }
    },
    async retryBonus(row) {
      try {
        await this.$confirm(`为绑定 #${row.id} 补发礼遇？`, '补发确认', { type: 'warning' })
      } catch {
        return
      }
      try {
        const res = await adminRetryReferralBonus(row.id)
        this.$message.success(res.message || '已补发')
        await this.fetchBindings()
      } catch (e) {
        this.$message.error(e.message || '补发失败')
      }
    },
    async clawbackBonus(row) {
      try {
        await this.$confirm(`冲正绑定 #${row.id} 已发礼遇？将从相关用户余额扣回。`, '冲正确认', { type: 'warning' })
      } catch {
        return
      }
      try {
        const data = await adminClawbackReferralBonus(row.id)
        this.$message.success(data.message || '已冲正')
        await this.fetchBindings()
        if (this.activeTab === 'audit') this.fetchAudit()
      } catch (e) {
        this.$message.error(e.message || '冲正失败')
      }
    },
    async cancelCommission(row) {
      try {
        await this.$confirm(`取消分润流水 #${row.id}（订单 ${row.orderNo}）？`, '取消分润', { type: 'warning' })
      } catch {
        return
      }
      try {
        const data = await adminCancelPartnerCommission({ ledgerId: row.id })
        this.$message.success(data.message || '已取消')
        await this.fetchCommission()
        if (this.activeTab === 'audit') this.fetchAudit()
      } catch (e) {
        this.$message.error(e.message || '取消失败')
      }
    },
    async fetchBindAttempts() {
      this.loadingBindAttempts = true
      try {
        const data = await fetchAdminReferralBindAttempts(this.attemptsPage, this.attemptsPageSize, {
          userId: Number(this.attemptFilters.userId) || undefined,
          clientIp: this.attemptFilters.clientIp?.trim() || undefined,
          couponNo: this.attemptFilters.couponNo?.trim() || undefined,
          success: this.attemptFilters.success || undefined
        })
        this.bindAttempts = data.items || []
        this.attemptsTotal = data.total || 0
      } catch (e) {
        this.$message.error(e.message || '加载失败')
      } finally {
        this.loadingBindAttempts = false
      }
    },
    revokeBinding(row) {
      this.revokeTarget = row
      this.revokeOptions = { clawbackBonus: true, cancelCommissions: true, skipBlockRebind: false }
      this.revokeDialogVisible = true
    },
    async confirmRevoke() {
      if (!this.revokeTarget) return
      this.revoking = true
      try {
        const res = await axios.post('/api/v1/admin/referral/bindings/revoke', {
          bindingId: this.revokeTarget.id,
          clawbackBonus: this.revokeOptions.clawbackBonus,
          cancelCommissions: this.revokeOptions.cancelCommissions,
          skipBlockRebind: this.revokeOptions.skipBlockRebind
        })
        if (res.data.code === 200) {
          this.$message.success(res.data.data?.message || res.data.message || '已撤销')
          this.revokeDialogVisible = false
          await this.fetchBindings()
          if (this.activeTab === 'audit') this.fetchAudit()
        } else {
          this.$message.error(res.data.message || '撤销失败')
        }
      } finally {
        this.revoking = false
      }
    }
  }
}
</script>

<style scoped>
.filter-input { width: 130px; }
.filter-select { width: 130px; margin-right: 8px; }
.tab-toolbar { margin-bottom: 12px; }
.settings-form { max-width: 520px; }
.field-hint { margin-left: 8px; color: #909399; font-size: 12px; }
.partner-grant { margin-top: 24px; padding-top: 16px; border-top: 1px solid #eee; }
.partner-grant__hint { margin: 0 0 12px; color: #909399; font-size: 13px; }
</style>
